import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { Recruit } from "../lib/api";
import { hasKey } from "../lib/settings";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";
import {
  Download, Play, Sparkles, FileText, AlertTriangle, Check, Trash2, ArrowLeft,
  Link2, Github, Linkedin, Globe, X as XIcon, FileDown, Upload, Search,
  Filter, RefreshCw, Briefcase, Eye, CheckSquare, PackageCheck, HelpCircle,
  Mail, Phone,
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

function classBadge(kind) {
  return {
    "Strong Fit": "badge badge-strong",
    "Moderate Fit": "badge badge-mod",
    "Weak Fit": "badge badge-weak",
    "High Risk": "badge badge-risk",
    "Hidden Gem": "badge badge-gem",
  }[kind] || "badge badge-mod";
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyzingSingleId, setAnalyzingSingleId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState("leaderboard");
  const [selectedCandIds, setSelectedCandIds] = useState([]);
  const [tab, setTab] = useState("match");
  const [kitBusy, setKitBusy] = useState(false);
  const [linksFor, setLinksFor] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { mode, candidateIds, label }
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [downloadModal, setDownloadModal] = useState(null); // { title, badge, subtitle, description, candidateScope, filename, url, targetBlank }
  const [kitBatchModal, setKitBatchModal] = useState(null); // { candidateList, label }
  const [kitBatchProgress, setKitBatchProgress] = useState(null); // { current, total, name }
  const [batchProgress, setBatchProgress] = useState(null);
  const [token, setToken] = useState(null);

  // Recruiter Filter & Sort states
  const [searchQuery, setSearchQuery] = useState("");
  const [classFilter, setClassFilter] = useState("ALL");
  const [minScoreFilter, setMinScoreFilter] = useState(0);
  const [sortBy, setSortBy] = useState("score_desc");

  const project = data?.project;
  const candidates = useMemo(() => data?.candidates || [], [data?.candidates]);
  const analyzed = useMemo(() => candidates.filter(c => c.analysis), [candidates]);
  const pendingCandidates = useMemo(() => candidates.filter(c => !c.analysis), [candidates]);
  const selected = useMemo(() => candidates.find(c => c.id === selectedId), [candidates, selectedId]);

  useEffect(() => {
    const fetchToken = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        setToken(session.access_token);
      }
    };
    fetchToken();
  }, []);

  const load = async () => {
    try {
      const d = await Recruit.getProject(id);
      setData(d);
      if (!selectedId && d.candidates.length) setSelectedId(d.candidates[0].id);
    } catch (e) {
      toast.error("Failed to load project");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  // Poll candidate details while the selected candidate's interview kit is generating
  useEffect(() => {
    if (!selected || selected.interview_kit?.status !== "processing") return;

    const interval = setInterval(async () => {
      try {
        const d = await Recruit.getProject(id);
        setData(d);
      } catch (e) {
        console.error("Polling project details failed:", e);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [id, selected, selectedId]);

  const stats = useMemo(() => {
    if (!analyzed.length) return null;
    const scores = analyzed.map(c => c.analysis.match_score || 0);
    const top = Math.max(...scores);
    const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    const strong = analyzed.filter(c => c.analysis.classification === "Strong Fit").length;
    const gems = analyzed.filter(c => c.analysis.classification === "Hidden Gem").length;
    const risk = analyzed.filter(c => c.analysis.classification === "High Risk").length;
    const confidence = Math.round((strong * 100 + (analyzed.length - strong - risk) * 50) / Math.max(analyzed.length, 1));
    return { top, avg, strong, gems, risk, confidence };
  }, [analyzed]);

  // Filtered and sorted candidates for recruiter discovery
  const filteredCandidates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = candidates.filter(c => {
      const a = c.analysis;
      if (classFilter === "PENDING") {
        if (a) return false;
      } else if (classFilter !== "ALL") {
        if (!a || a.classification !== classFilter) return false;
      }

      if (minScoreFilter > 0) {
        if (!a || (a.match_score || 0) < minScoreFilter) return false;
      }

      if (q) {
        const nameStr = (a?.name || "").toLowerCase();
        const fileStr = (c.filename || "").toLowerCase();
        const headStr = (a?.headline || "").toLowerCase();
        const eduStr = (a?.education || "").toLowerCase();
        const skillsStr = (a?.skills || []).join(" ").toLowerCase();
        const sumStr = (a?.summary || "").toLowerCase();
        if (
          !nameStr.includes(q) &&
          !fileStr.includes(q) &&
          !headStr.includes(q) &&
          !eduStr.includes(q) &&
          !skillsStr.includes(q) &&
          !sumStr.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });

    list = [...list].sort((x, y) => {
      const ax = x.analysis;
      const ay = y.analysis;
      if (sortBy === "score_desc") {
        const sx = ax ? (ax.match_score ?? -1) : -1;
        const sy = ay ? (ay.match_score ?? -1) : -1;
        return sy - sx;
      }
      if (sortBy === "exp_desc") {
        const ex = ax ? (ax.years_experience ?? -1) : -1;
        const ey = ay ? (ay.years_experience ?? -1) : -1;
        return ey - ex;
      }
      if (sortBy === "risk_asc") {
        const rx = ax ? (ax.risk_score ?? 999) : 999;
        const ry = ay ? (ay.risk_score ?? 999) : 999;
        return rx - ry;
      }
      if (sortBy === "name_asc") {
        const nx = (ax?.name || x.filename || "").toLowerCase();
        const ny = (ay?.name || y.filename || "").toLowerCase();
        return nx.localeCompare(ny);
      }
      return 0;
    });

    return list;
  }, [candidates, searchQuery, classFilter, minScoreFilter, sortBy]);

  // Classification counts for filter pills
  const classCounts = useMemo(() => {
    const counts = {
      ALL: candidates.length,
      "Strong Fit": 0,
      "Hidden Gem": 0,
      "Moderate Fit": 0,
      "Weak Fit": 0,
      "High Risk": 0,
      PENDING: 0,
    };
    candidates.forEach(c => {
      if (!c.analysis) counts.PENDING += 1;
      else if (counts[c.analysis.classification] !== undefined) {
        counts[c.analysis.classification] += 1;
      }
    });
    return counts;
  }, [candidates]);

  // Download Trigger Helper
  const triggerDownloadConfirmed = () => {
    if (!downloadModal?.url) return;
    const { url, targetBlank } = downloadModal;
    setDownloadModal(null);
    if (targetBlank) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Download Handlers (open explanatory confirmation modal)
  const promptExportCsv = (candidateIds = null) => {
    const isFiltered = candidateIds?.length > 0;
    const count = isFiltered ? candidateIds.length : analyzed.length;
    setDownloadModal({
      title: "Export Ranking Spreadsheet",
      badge: "CSV SPREADSHEET",
      subtitle: `Excel & ATS-compatible tabular dataset`,
      candidateScope: isFiltered ? `${count} selected candidate(s)` : `All ${count} analyzed candidates`,
      description: "Generates a structured CSV file including candidate rankings, match scores, classifications, risk scores, years of experience, education, top skills, missing requirements, strengths, weaknesses, recruiter verdicts, and profile links.",
      url: Recruit.exportCsvUrl(id, token, candidateIds),
      targetBlank: false,
    });
  };

  const promptExportPdf = (candidateIds = null) => {
    const isFiltered = candidateIds?.length > 0;
    const count = isFiltered ? candidateIds.length : analyzed.length;
    setDownloadModal({
      title: "Export Project Evaluation Report",
      badge: "EXECUTIVE PDF REPORT",
      subtitle: `Publication-ready executive hiring intelligence report`,
      candidateScope: isFiltered ? `${count} selected candidate(s)` : `All ${count} analyzed candidates`,
      description: "Generates an editorial PDF document containing the executive summary, pool statistics, ranked leaderboard table, and comprehensive per-candidate profiles with dimension score breakdowns, strengths, weaknesses, and interview questions.",
      url: Recruit.exportPdfUrl(id, token, candidateIds),
      targetBlank: true,
    });
  };

  const promptExportPackZip = (candidateIds = null) => {
    const isFiltered = candidateIds?.length > 0;
    const count = isFiltered ? candidateIds.length : analyzed.length;
    setDownloadModal({
      title: "Download Interviewer Handoff Pack (ZIP)",
      badge: "STRUCTURED ZIP ARCHIVE",
      subtitle: `Everything your hiring managers and interviewers need in one archive`,
      candidateScope: isFiltered ? `${count} selected candidate(s)` : `All ${count} analyzed candidates`,
      description: "Bundles: (1) Interactive HTML Dashboard (with live search, filters, and offline links to each candidate's local resume), (2) Full Shortlist Summary Report PDF, (3) Ranking Spreadsheet CSV, and (4) Individual candidate folders containing both their original uploaded Resume and their AI Evaluation Brief PDF.",
      url: Recruit.exportPackZipUrl(id, token, candidateIds),
      targetBlank: false,
    });
  };

  const promptExportHtml = (candidateIds = null) => {
    const isFiltered = candidateIds?.length > 0;
    const count = isFiltered ? candidateIds.length : analyzed.length;
    setDownloadModal({
      title: "Export Interactive HTML Report",
      badge: "INTERACTIVE DASHBOARD (.HTML)",
      subtitle: `Self-contained, offline-ready interactive hiring intelligence report`,
      candidateScope: isFiltered ? `${count} selected candidate(s)` : `All ${count} analyzed candidates`,
      description: "Generates a standalone single-file HTML report featuring live keyword search, fit-classification filter pills, candidate leaderboard sidebar, collapsible job description, and full candidate dossiers with tailored interview kits, interviewer scratchpad, and direct resume links.",
      url: Recruit.exportHtmlUrl(id, token, candidateIds),
      targetBlank: true,
    });
  };

  const promptCandidateBriefPdf = (cand) => {
    const name = cand.analysis?.name || cand.filename;
    setDownloadModal({
      title: `Download AI Brief — ${name}`,
      badge: "CANDIDATE INTERVIEW BRIEF",
      subtitle: `Single-candidate PDF dossier for interviewer review`,
      candidateScope: `1 Candidate (${name})`,
      description: "Generates a standalone PDF containing the candidate's match score breakdown, strengths, risk areas to probe, verification points, and custom interview questions to guide your interview.",
      url: Recruit.candidateBriefUrl(id, cand.id, token),
      targetBlank: true,
    });
  };

  const promptCandidateResume = (cand, inline = false) => {
    const name = cand.analysis?.name || cand.filename;
    setDownloadModal({
      title: inline ? `View Resume — ${name}` : `Download Resume — ${name}`,
      badge: "ORIGINAL RESUME FILE",
      subtitle: `File: ${cand.filename}`,
      candidateScope: `1 Candidate (${name})`,
      description: inline
        ? "Opens the candidate's original uploaded resume file in a browser preview tab."
        : "Downloads the exact original resume file (PDF/DOCX) originally uploaded to this hiring project.",
      url: Recruit.candidateResumeUrl(id, cand.id, token, inline),
      targetBlank: inline,
    });
  };

  // Batch Interview Kit Generation Handlers
  const promptBatchInterviewKits = (candidateList, label) => {
    if (!candidateList.length) return;
    setKitBatchModal({
      candidates: candidateList,
      label,
    });
  };

  const executeBatchKits = async () => {
    if (!kitBatchModal?.candidates?.length) return;
    const targetCands = kitBatchModal.candidates;
    setKitBatchModal(null);
    setKitBusy(true);

    try {
      for (let i = 0; i < targetCands.length; i++) {
        const c = targetCands[i];
        const cName = c.analysis?.name || c.filename;
        setKitBatchProgress({
          current: i + 1,
          total: targetCands.length,
          name: cName,
        });

        try {
          await Recruit.interviewKit(id, c.id);
        } catch (err) {
          console.error(`Failed kit for ${c.id}:`, err);
        }
        await load();
      }
      toast.success(`Generated interview kits for ${targetCands.length} candidate(s)!`);
    } catch (e) {
      toast.error("Batch interview kit generation encountered an issue.");
    } finally {
      setKitBatchProgress(null);
      setKitBusy(false);
      await load();
    }
  };

  const onAnalyzeAllPendingClick = () => {
    if (pendingCandidates.length === 0) {
      toast.info("All candidates in this project have already been analyzed.");
      return;
    }
    setConfirmModal({
      mode: "pending",
      candidateIds: pendingCandidates.map(c => c.id),
      label: `${pendingCandidates.length} pending candidate(s)`,
    });
  };

  const onAnalyzeSelectedClick = () => {
    if (selectedCandIds.length === 0) return;
    setConfirmModal({
      mode: "selected",
      candidateIds: [...selectedCandIds],
      label: `${selectedCandIds.length} selected candidate(s)`,
    });
  };

  const onAnalyzeSingleClick = (cand) => {
    const name = cand.analysis?.name || cand.filename;
    setConfirmModal({
      mode: "single",
      candidateIds: [cand.id],
      label: `${name}`,
    });
  };

  const executeAnalysis = async () => {
    if (!confirmModal) return;
    const { mode, candidateIds } = confirmModal;
    setConfirmModal(null);

    if (mode === "single" && candidateIds.length === 1) {
      const targetCid = candidateIds[0];
      setAnalyzingSingleId(targetCid);
      setBusy(true);
      try {
        const res = await Recruit.analyzeCandidate(id, targetCid);
        if (res.analyzed > 0) {
          toast.success("Candidate analysis completed!");
        } else if (res.errors?.length) {
          toast.error(`Analysis failed: ${res.errors[0].error || "Unknown error"}`);
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e.message;
        if (status === 402) {
          toast.error("Free trial used up. Add your own AI key in Settings.");
          nav("/app/settings");
        } else {
          toast.error("Analysis failed: " + detail);
        }
      } finally {
        setAnalyzingSingleId(null);
        setBusy(false);
        await load();
      }
      return;
    }

    // Batch execution (3 candidates per batch to stay well below API Gateway's 29s timeout)
    setBusy(true);
    const BATCH_SIZE = 3;
    const targetList = [...candidateIds];
    const totalToProcess = targetList.length;
    const totalBatches = Math.ceil(totalToProcess / BATCH_SIZE);
    let totalProcessed = 0;
    let totalErrors = [];

    try {
      for (let i = 0; i < targetList.length; i += BATCH_SIZE) {
        const chunkIds = targetList.slice(i, i + BATCH_SIZE);
        const currentBatchNumber = Math.floor(i / BATCH_SIZE) + 1;

        setBatchProgress({
          current: currentBatchNumber,
          total: totalBatches,
          processed: totalProcessed,
          totalCandidates: totalToProcess,
        });

        try {
          const res = await Recruit.analyze(id, {
            batchSize: chunkIds.length,
            candidateIds: chunkIds,
          });
          totalProcessed += res.analyzed || 0;
          if (res.errors?.length) {
            totalErrors = [...totalErrors, ...res.errors];
          }
        } catch (batchErr) {
          const status = batchErr?.response?.status;
          if (status === 402) {
            throw batchErr;
          }
          totalErrors.push({ error: batchErr?.response?.data?.detail || batchErr.message });
        }

        await load();
      }

      if (totalProcessed > 0) {
        toast.success(`Analysis completed! Processed ${totalProcessed} candidate(s).`);
      }
      if (totalErrors.length > 0) {
        toast.error(`${totalErrors.length} candidate(s) encountered an error during analysis.`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 402) {
        toast.error("Insufficient credits. Claim free Beta Credits or add your AI key in Settings.");
        nav("/app/settings");
      } else {
        toast.error("Analysis stopped: " + detail);
      }
    } finally {
      setBatchProgress(null);
      await load();
      setBusy(false);
    }
  };

  const onKit = async () => {
    if (!selected || !selected.analysis) return;
    setKitBusy(true);
    try {
      await Recruit.interviewKit(id, selected.id);
      toast.success("Interview kit generated!");
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 402) {
        toast.error("Free trial used up. Add your own AI key in Settings.");
        nav("/app/settings");
      } else {
        toast.error("Kit failed: " + detail);
      }
    } finally {
      setKitBusy(false);
    }
  };

  const confirmDeleteCandidate = async () => {
    if (!deleteTargetId) return;
    const cid = deleteTargetId;
    setDeleteTargetId(null);
    try {
      await Recruit.deleteCandidate(id, cid);
      if (selectedId === cid) setSelectedId(null);
      setSelectedCandIds(prev => prev.filter(x => x !== cid));
      toast.success("Candidate removed");
      await load();
    } catch (e) {
      toast.error("Failed to remove candidate");
    }
  };

  const toggleSelectCand = (cid) => {
    setSelectedCandIds(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  };

  const toggleSelectAllFiltered = () => {
    const filteredIds = filteredCandidates.map(c => c.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(fid => selectedCandIds.includes(fid));
    if (allSelected) {
      setSelectedCandIds(prev => prev.filter(cid => !filteredIds.includes(cid)));
    } else {
      setSelectedCandIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const selectAllStrongFits = () => {
    const strongIds = candidates
      .filter(c => c.analysis && (c.analysis.classification === "Strong Fit" || c.analysis.classification === "Hidden Gem"))
      .map(c => c.id);
    if (!strongIds.length) {
      toast.info("No Strong Fit or Hidden Gem candidates found yet.");
      return;
    }
    setSelectedCandIds(strongIds);
    toast.success(`Selected ${strongIds.length} top fit candidate(s)`);
  };

  const compareCandidates = useMemo(() => {
    return candidates.filter(c => selectedCandIds.includes(c.id) && c.analysis).slice(0, 4);
  }, [candidates, selectedCandIds]);

  const selectedAnalyzedCandidates = useMemo(() => {
    return candidates.filter(c => selectedCandIds.includes(c.id) && c.analysis);
  }, [candidates, selectedCandIds]);

  if (!project) return (
    <div className="min-h-screen bg-canvas text-ink">
      <Nav />
      <div className="max-w-[1280px] mx-auto px-6 py-12 flex items-center gap-2 text-xs text-soft">
        <span className="dot"/><span className="dot"/><span className="dot"/> Loading project details
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="project-detail-page">
      <Nav />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 py-10">
        {/* Header */}
        <Link to="/app" className="text-xs font-medium uppercase tracking-wider text-sub hover:text-ink inline-flex items-center gap-1.5 mb-5 transition">
          <ArrowLeft size={12} /> All projects
        </Link>
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div className="min-w-0">
            <div className="eyebrow mb-2">{project.role_title || "Open role"}</div>
            <h1 className="display-1 text-4xl lg:text-5xl text-ink">{project.title}</h1>
            {!hasKey() && (
              <div className="mt-3 inline-flex items-center gap-2 bg-accentSoft text-accent px-3 py-1.5 rounded-full text-xs font-medium">
                <Sparkles size={12} /> Free trial mode · AI Screening
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button 
              onClick={() => setShowUploadModal(true)} 
              data-testid="add-resumes-btn"
              className="btn-ghost flex items-center gap-2 text-xs py-2 px-4 border hairline bg-white hover:bg-haze text-ink font-medium"
            >
              <Upload size={14} className="text-accent" /> Add resumes
            </button>
            {token && analyzed.length > 0 && (
              <>
                <button
                  onClick={() => promptExportCsv(null)}
                  data-testid="export-csv-btn" 
                  className="btn-ghost flex items-center gap-2 text-xs py-2 px-3.5"
                  title="Download full ranking CSV"
                >
                  <FileDown size={14} /> CSV
                </button>
                <button
                  onClick={() => promptExportHtml(null)}
                  data-testid="export-html-btn"
                  className="btn-ghost flex items-center gap-2 text-xs py-2 px-3.5 border hairline bg-white hover:bg-haze text-ink font-medium"
                  title="Download interactive standalone HTML report"
                >
                  <Globe size={14} className="text-accent" /> HTML Report
                </button>
                <button
                  onClick={() => promptExportPdf(null)}
                  data-testid="export-pdf-btn"
                  className="btn-ghost flex items-center gap-2 text-xs py-2 px-3.5"
                  title="Download full project PDF report"
                >
                  <Download size={14} /> PDF Report
                </button>
                <button
                  onClick={() => promptExportPackZip(null)}
                  data-testid="export-pack-btn"
                  className="btn-ghost flex items-center gap-2 text-xs py-2 px-3.5 border hairline bg-white hover:bg-haze text-ink font-medium"
                  title="Download ZIP with Summary Report, CSV, and all Candidate Resumes + AI Briefs"
                >
                  <PackageCheck size={14} className="text-accent" /> Interviewer Pack (ZIP)
                </button>
              </>
            )}
            <button onClick={onAnalyzeAllPendingClick} disabled={busy || pendingCandidates.length === 0} data-testid="analyze-btn"
              className="btn-accent flex items-center gap-2 text-xs py-2.5 px-5 disabled:opacity-50">
              {busy ? (
                <>
                  <span className="dot"/><span className="dot"/><span className="dot"/> 
                  {batchProgress ? `Analyzing batch ${batchProgress.current}/${batchProgress.total}...` : "Analyzing..."}
                </>
              ) : (
                <>
                  <Play size={14} /> 
                  {pendingCandidates.length > 0 
                    ? `Run analysis (${pendingCandidates.length} pending)` 
                    : "All candidates analyzed"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Batch Progress Banner if analysis active */}
        {batchProgress && (
          <div className="mb-6 card p-4 bg-accentSoft/30 border border-accent/20 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="dot animate-ping"/><span className="dot"/><span className="dot"/>
              <div>
                <p className="text-xs font-semibold text-ink">
                  Running batch {batchProgress.current} of {batchProgress.total}
                </p>
                <p className="text-[11px] font-mono text-sub">
                  Processed {batchProgress.processed} of {batchProgress.totalCandidates} candidates (3 at a time for fast, reliable completion)...
                </p>
              </div>
            </div>
            <span className="text-xs font-mono font-bold text-accent">
              {Math.round((batchProgress.processed / Math.max(batchProgress.totalCandidates, 1)) * 100)}%
            </span>
          </div>
        )}

        {/* Batch Interview Kit Progress Banner if active */}
        {kitBatchProgress && (
          <div className="mb-6 card p-4 bg-accentSoft/40 border border-accent/30 flex items-center justify-between gap-4 shadow-soft">
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-accent animate-spin" />
              <div>
                <p className="text-xs font-semibold text-ink">
                  Generating Interview Kit {kitBatchProgress.current} of {kitBatchProgress.total}
                </p>
                <p className="text-[11px] font-mono text-sub">
                  Target candidate: <strong className="text-ink">{kitBatchProgress.name}</strong> — crafting customized questions & reference criteria...
                </p>
              </div>
            </div>
            <span className="text-xs font-mono font-bold text-accent">
              {Math.round((kitBatchProgress.current / Math.max(kitBatchProgress.total, 1)) * 100)}%
            </span>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              ["Total Pool", `${analyzed.length} / ${candidates.length} analyzed`, "text-ink"],
              ["Top Match", `${stats.top}/100`, "text-accent"],
              ["Average Score", `${stats.avg}/100`, "text-ink"],
              ["Strong Fits", stats.strong + (stats.gems ? ` (+${stats.gems} Gem)` : ""), "text-success"],
              ["Pool Confidence", `${stats.confidence}%`, "text-ink"],
            ].map(([k, v, c]) => (
              <div key={k} className="card p-5 bg-white">
                <div className="text-[10px] font-mono uppercase tracking-wider text-soft">{k}</div>
                <div className={`font-semibold text-2xl mt-1 tracking-tight ${c}`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Recruiter Search, Filter & Shortlist Toolbar */}
        {candidates.length > 0 && (
          <div className="card p-4 bg-white mb-6 space-y-3.5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Search Box */}
              <div className="relative flex-1 min-w-[240px]">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-soft" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by candidate name, skill (e.g. Python, AWS), role, education..."
                  className="input-apple pl-9 pr-8 py-2 text-xs w-full"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-soft hover:text-ink"
                  >
                    <XIcon size={13} />
                  </button>
                )}
              </div>

              {/* Min Score Filter */}
              <div className="flex items-center gap-2">
                <Filter size={13} className="text-soft" />
                <select
                  value={minScoreFilter}
                  onChange={(e) => setMinScoreFilter(Number(e.target.value))}
                  className="border hairline rounded-xl px-3 py-2 text-xs bg-white text-ink font-mono"
                >
                  <option value={0}>Score: All</option>
                  <option value={80}>Score: 80+ (Top)</option>
                  <option value={70}>Score: 70+</option>
                  <option value={60}>Score: 60+</option>
                  <option value={50}>Score: 50+</option>
                </select>

                {/* Sort Selector */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border hairline rounded-xl px-3 py-2 text-xs bg-white text-ink font-mono"
                >
                  <option value="score_desc">Sort: Highest Match</option>
                  <option value="exp_desc">Sort: Most Experience</option>
                  <option value="risk_asc">Sort: Lowest Risk</option>
                  <option value="name_asc">Sort: Name (A–Z)</option>
                </select>

                {analyzed.length > 0 && (
                  <button
                    onClick={selectAllStrongFits}
                    className="btn-ghost text-xs py-2 px-3 border hairline bg-haze/50 hover:bg-haze text-ink flex items-center gap-1.5"
                    title="Auto-select all Strong Fit and Hidden Gem candidates for interviewer handoff"
                  >
                    <CheckSquare size={13} className="text-accent" /> Select Top Fits
                  </button>
                )}
              </div>
            </div>

            {/* Classification Filter Pills */}
            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t hairline">
              {[
                ["ALL", `All (${classCounts.ALL})`],
                ["Strong Fit", `Strong Fit (${classCounts["Strong Fit"]})`],
                ["Hidden Gem", `Hidden Gem (${classCounts["Hidden Gem"]})`],
                ["Moderate Fit", `Moderate Fit (${classCounts["Moderate Fit"]})`],
                ["Weak Fit", `Weak Fit (${classCounts["Weak Fit"]})`],
                ["High Risk", `High Risk (${classCounts["High Risk"]})`],
                ["PENDING", `Pending Analysis (${classCounts.PENDING})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setClassFilter(key)}
                  className={`px-3 py-1 rounded-full text-[11px] font-mono transition-all ${
                    classFilter === key
                      ? "bg-ink text-white font-semibold"
                      : "bg-haze text-sub hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
              {(searchQuery || classFilter !== "ALL" || minScoreFilter > 0) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setClassFilter("ALL");
                    setMinScoreFilter(0);
                  }}
                  className="text-[11px] font-mono text-accent hover:underline ml-auto"
                >
                  Reset filters ({filteredCandidates.length} shown)
                </button>
              )}
            </div>
          </div>
        )}

        {/* Multi-Select Bulk Action Bar for Interviewer Handoff */}
        {selectedCandIds.length > 0 && (
          <div className="mb-6 card p-4 bg-ink text-white flex items-center justify-between gap-4 flex-wrap shadow-lift">
            <div className="flex items-center gap-3">
              <span className="bg-accent text-white font-mono text-xs font-bold px-2.5 py-1 rounded-full">
                {selectedCandIds.length} selected
              </span>
              <span className="text-xs text-white/80">
                Action bar for selected candidates: generate interview kits, compare, or download interviewer packets.
              </span>
              <button
                onClick={() => setSelectedCandIds([])}
                className="text-xs font-mono text-white/60 hover:text-white underline ml-2"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Batch Interview Kit Generator */}
              {selectedAnalyzedCandidates.length > 0 && (
                <button
                  onClick={() => promptBatchInterviewKits(selectedAnalyzedCandidates, `${selectedAnalyzedCandidates.length} selected candidate(s)`)}
                  disabled={kitBusy}
                  className="bg-accent hover:bg-accent/90 text-white text-xs font-medium py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
                  title="Generate customized interview questions & evaluation rubrics for selected candidates"
                >
                  <Sparkles size={13} /> Generate Interview Kits ({selectedAnalyzedCandidates.length})
                </button>
              )}

              {/* Analyze Selected */}
              <button
                onClick={onAnalyzeSelectedClick}
                disabled={busy}
                className="bg-white/10 hover:bg-white/20 text-white text-xs py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
              >
                <Play size={12} /> Analyze Selected ({selectedCandIds.length})
              </button>

              {/* Compare */}
              {compareCandidates.length >= 2 && (
                <button
                  onClick={() => setView("compare")}
                  className="bg-white/10 hover:bg-white/20 text-white text-xs py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
                >
                  Compare ({compareCandidates.length})
                </button>
              )}

              {/* Export Actions with Modal Confirmation */}
              {token && (
                <>
                  <button
                    onClick={() => promptExportCsv(selectedCandIds)}
                    className="bg-white/10 hover:bg-white/20 text-white text-xs py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
                  >
                    <FileDown size={12} /> Selected CSV
                  </button>
                  <button
                    onClick={() => promptExportHtml(selectedCandIds)}
                    className="bg-white/10 hover:bg-white/20 text-white text-xs py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
                  >
                    <Globe size={12} /> Selected HTML
                  </button>
                  <button
                    onClick={() => promptExportPdf(selectedCandIds)}
                    className="bg-white/10 hover:bg-white/20 text-white text-xs py-2 px-3.5 rounded-full flex items-center gap-1.5 transition"
                  >
                    <Download size={12} /> Selected PDF
                  </button>
                  <button
                    onClick={() => promptExportPackZip(selectedCandIds)}
                    className="bg-white hover:bg-white/90 text-ink text-xs font-medium py-2 px-4 rounded-full flex items-center gap-1.5 transition"
                  >
                    <PackageCheck size={13} className="text-accent" /> Interviewer Pack (ZIP)
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* View Switcher Tabs */}
        <div className="flex gap-1.5 mb-6 border-b hairline pb-px">
          {[
            ["leaderboard", `Leaderboard (${filteredCandidates.length})`],
            ["profile", "Candidate Deep-Dive"],
            ["compare", `Compare ${compareCandidates.length ? `(${compareCandidates.length})` : ""}`]
          ].map(([k, l]) => (
            <button 
              key={k} 
              data-testid={`view-${k}`} 
              onClick={() => setView(k)} 
              className={`px-4 py-2 text-xs font-medium uppercase tracking-wider border-t border-x transition-all -mb-px rounded-t-xl ${
                view === k 
                  ? "bg-ink text-white border-ink" 
                  : "bg-white text-sub border-transparent hover:bg-haze"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* LEADERBOARD VIEW */}
        {view === "leaderboard" && (
          <div className="card overflow-hidden shadow-soft mb-8 bg-white">
            <div className="grid grid-cols-12 gap-3 px-5 py-3.5 bg-haze border-b hairline text-[10px] font-mono uppercase tracking-wider text-sub items-center">
              <div className="col-span-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filteredCandidates.length > 0 && filteredCandidates.every(c => selectedCandIds.includes(c.id))}
                  onChange={toggleSelectAllFiltered}
                  className="w-4 h-4 accent-accent rounded border-line cursor-pointer"
                  title="Select all filtered candidates"
                />
                <span>#</span>
              </div>
              <div className="col-span-3">Candidate & Role</div>
              <div className="col-span-1">Match</div>
              <div className="col-span-2">Classification</div>
              <div className="col-span-2">Skills & Experience</div>
              <div className="col-span-1">Profiles</div>
              <div className="col-span-2 text-right">Interviewer Actions</div>
            </div>
            {candidates.length === 0 && (
              <div className="p-12 text-center">
                <p className="font-mono text-xs text-soft mb-3">No candidates uploaded yet.</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="btn-accent text-xs py-2 px-4 inline-flex items-center gap-1.5"
                >
                  <Upload size={12} /> Add resumes
                </button>
              </div>
            )}
            {candidates.length > 0 && filteredCandidates.length === 0 && (
              <div className="p-12 text-center">
                <p className="font-mono text-xs text-soft">No candidates match the current filters.</p>
              </div>
            )}
            {filteredCandidates.map((c, idx) => {
              const a = c.analysis;
              const lk = c.links || {};
              const linkCount = ["linkedin", "github", "portfolio"].filter(k => lk[k]).length;
              const isSelected = selectedCandIds.includes(c.id);
              const isAnalyzingThis = analyzingSingleId === c.id;

              return (
                <div
                  key={c.id}
                  className={`grid grid-cols-12 gap-3 px-5 py-4 border-b hairline last:border-0 items-center transition ${
                    isSelected ? "bg-accentSoft/25" : "hover:bg-haze/60"
                  }`}
                  data-testid={`row-${c.id}`}
                >
                  <div className="col-span-1 flex items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectCand(c.id)}
                      data-testid={`select-${c.id}`}
                      className="w-4 h-4 accent-accent rounded border-line cursor-pointer"
                    />
                    <span className="font-mono text-soft text-xs">
                      {a ? (c.rank || idx + 1).toString().padStart(2, "0") : "—"}
                    </span>
                  </div>

                  <div className="col-span-3 min-w-0">
                    <button
                      onClick={() => { setSelectedId(c.id); setView("profile"); }}
                      data-testid={`open-${c.id}`}
                      className="text-left w-full group"
                    >
                      <div className="font-serif font-light text-lg text-ink group-hover:text-accent group-hover:underline truncate">
                        {a?.name || c.filename.replace(/\.[^.]+$/, "")}
                      </div>
                      {a?.headline ? (
                        <div className="text-xs text-sub truncate">{a.headline}</div>
                      ) : (
                        <div className="text-[10px] font-mono text-soft truncate">{c.filename}</div>
                      )}
                      {a?.education && (
                        <div className="text-[10px] font-mono text-soft truncate mt-0.5">{a.education}</div>
                      )}
                    </button>
                  </div>

                  <div className="col-span-1">
                    {a ? (
                      <div>
                        <div className="font-mono text-xl font-bold text-accent tabular-nums">{a.match_score}</div>
                        <div className="text-[10px] font-mono text-soft">Risk {a.risk_score ?? "—"}</div>
                      </div>
                    ) : (
                      <span className="text-soft text-sm font-mono">—</span>
                    )}
                  </div>

                  <div className="col-span-2">
                    {a ? (
                      <div className="space-y-1">
                        <span className={classBadge(a.classification)}>{a.classification}</span>
                        {a.years_experience !== undefined && a.years_experience !== null && (
                          <div className="text-[10px] font-mono text-sub flex items-center gap-1">
                            <Briefcase size={10} /> {a.years_experience} yrs exp
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="badge badge-weak text-[10px] font-mono">Pending</span>
                    )}
                  </div>

                  <div className="col-span-2 min-w-0">
                    {a ? (
                      <div>
                        <div className="text-xs font-sans text-ink truncate" title={(a.skills || []).join(", ")}>
                          {(a.skills || []).slice(0, 4).join(" · ") || "—"}
                        </div>
                        {a.missing_requirements?.length > 0 && (
                          <div className="text-[10px] font-mono text-danger truncate mt-0.5" title={`Missing: ${a.missing_requirements.join(", ")}`}>
                            Missing: {a.missing_requirements.slice(0, 2).join(", ")}
                            {a.missing_requirements.length > 2 ? ` +${a.missing_requirements.length - 2}` : ""}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-soft font-mono">Not screened yet</span>
                    )}
                  </div>

                  <div className="col-span-1 flex items-center gap-1.5 flex-wrap">
                    {lk.linkedin && (
                      <a href={lk.linkedin} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg bg-haze hover:bg-ink hover:text-white text-sub transition" title="LinkedIn">
                        <Linkedin size={12} />
                      </a>
                    )}
                    {lk.github && (
                      <a href={lk.github} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg bg-haze hover:bg-ink hover:text-white text-sub transition" title="GitHub">
                        <Github size={12} />
                      </a>
                    )}
                    {lk.portfolio && (
                      <a href={lk.portfolio} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg bg-haze hover:bg-ink hover:text-white text-sub transition" title={`Portfolio: ${lk.portfolio}`}>
                        <Globe size={12} />
                      </a>
                    )}
                    <button
                      onClick={() => setLinksFor(c.id)}
                      data-testid={`links-${c.id}`}
                      className="text-[10px] px-2 py-1 border hairline rounded-full flex items-center gap-1 transition-all hover:bg-ink hover:text-white bg-white text-sub"
                      title="Edit candidate links"
                    >
                      <Link2 size={10} /> {linkCount === 0 ? "Add" : ""}
                    </button>
                  </div>

                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    {/* Single Candidate Analyze / Re-analyze Button */}
                    <button
                      onClick={() => onAnalyzeSingleClick(c)}
                      disabled={busy}
                      data-testid={`analyze-single-${c.id}`}
                      className={`text-[11px] font-mono px-2.5 py-1.5 rounded-lg flex items-center gap-1 transition ${
                        !a
                          ? "bg-accent text-white hover:bg-accent/90 font-medium"
                          : "border hairline bg-white hover:bg-haze text-sub hover:text-ink"
                      }`}
                      title={a ? "Re-run AI analysis for this candidate" : "Run AI analysis for this candidate"}
                    >
                      {isAnalyzingThis ? (
                        <RefreshCw size={11} className="animate-spin" />
                      ) : a ? (
                        <RefreshCw size={11} />
                      ) : (
                        <>
                          <Play size={10} /> Analyze
                        </>
                      )}
                    </button>

                    {/* Direct Original Resume Download with Modal Confirmation */}
                    {token && (
                      <button
                        onClick={() => promptCandidateResume(c, false)}
                        className="p-1.5 border hairline rounded-lg bg-white hover:bg-haze text-sub hover:text-ink transition"
                        title="Download Original Resume"
                        data-testid={`resume-dl-${c.id}`}
                      >
                        <FileText size={13} />
                      </button>
                    )}

                    {/* Direct AI Evaluation Brief PDF Download with Modal Confirmation */}
                    {token && a && (
                      <button
                        onClick={() => promptCandidateBriefPdf(c)}
                        className="p-1.5 border hairline rounded-lg bg-white hover:bg-haze text-accent transition"
                        title="Download Candidate AI Evaluation Brief (PDF)"
                        data-testid={`brief-dl-${c.id}`}
                      >
                        <Download size={13} />
                      </button>
                    )}

                    {/* Remove Candidate */}
                    <button
                      onClick={() => setDeleteTargetId(c.id)}
                      className="text-soft hover:text-danger p-1.5 bg-white hover:bg-haze rounded-lg transition"
                      data-testid={`delete-${c.id}`}
                      title="Remove candidate"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CANDIDATE PROFILE VIEW */}
        {view === "profile" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <aside className="lg:col-span-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 space-y-2">
              {filteredCandidates.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  data-testid={`pick-${c.id}`}
                  className={`w-full text-left border p-4 bg-white transition-all rounded-xl ${
                    selectedId === c.id 
                      ? "border-accent bg-accentSoft/30 shadow-soft" 
                      : "border-hairline hover:bg-haze"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-mono text-soft">
                      {c.analysis ? `#${c.rank || "—"}` : "Pending"}
                    </span>
                    {c.analysis && (
                      <span className={classBadge(c.analysis.classification)}>
                        {c.analysis.classification.replace(" Fit", "")}
                      </span>
                    )}
                  </div>
                  <div className="font-serif font-light text-lg text-ink leading-snug truncate">
                    {c.analysis?.name || c.filename.replace(/\.[^.]+$/, "")}
                  </div>
                  {c.analysis?.headline && (
                    <div className="text-[11px] text-sub truncate mt-0.5">{c.analysis.headline}</div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-lg font-bold text-accent tabular-nums">
                      {c.analysis?.match_score ?? "—"}
                    </span>
                    {c.analysis?.years_experience !== undefined && (
                      <span className="text-[10px] font-mono text-soft">{c.analysis.years_experience}y exp</span>
                    )}
                  </div>
                </button>
              ))}
            </aside>

            <section className="lg:col-span-9">
              <div className="card p-8 bg-white">
                {!selected ? (
                  <div className="font-mono text-xs text-soft">Select a candidate from the sidebar.</div>
                ) : !selected.analysis ? (
                  <div className="space-y-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <span className="badge badge-weak text-xs">Pending AI Analysis</span>
                        <h2 className="text-2xl font-serif font-light text-ink mt-2">{selected.filename}</h2>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {token && (
                          <>
                            <button
                              onClick={() => promptCandidateResume(selected, true)}
                              className="btn-ghost py-2 px-3.5 text-xs inline-flex items-center gap-1.5 border hairline"
                            >
                              <Eye size={13} /> View Resume
                            </button>
                            <button
                              onClick={() => promptCandidateResume(selected, false)}
                              className="btn-ghost py-2 px-3.5 text-xs inline-flex items-center gap-1.5 border hairline"
                            >
                              <FileText size={13} /> Download Resume
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => onAnalyzeSingleClick(selected)}
                          disabled={busy}
                          className="btn-accent py-2 px-5 text-xs inline-flex items-center gap-1.5"
                        >
                          <Play size={13} /> {analyzingSingleId === selected.id ? "Analyzing..." : "Analyze This Candidate"}
                        </button>
                      </div>
                    </div>

                    {selected.links?.security_status && (selected.links.security_status.white_ink_detected || selected.links.security_status.prompt_injection_detected) && (
                      <div className="p-4 border border-dashed border-danger bg-danger/5 rounded-xl space-y-2 max-w-xl">
                        <div className="flex items-center gap-2 text-danger font-mono text-xs font-bold uppercase tracking-wider">
                          <AlertTriangle size={14} /> Security Flag Detected
                        </div>
                        {selected.links.security_status.prompt_injection_detected && (
                          <p className="text-xs text-ink font-sans">
                            ⚠️ <strong>Potential Prompt Injection:</strong> This resume contains text matching adversarial instruction templates.
                          </p>
                        )}
                        {selected.links.security_status.white_ink_detected && selected.links.security_status.hidden_words && selected.links.security_status.hidden_words.length > 0 && (
                          <p className="text-xs text-ink font-sans">
                            ⚠️ <strong>Hidden Text / Keyword Stuffing:</strong> Detected white/hidden text matching keywords: <code className="bg-haze px-1.5 py-0.5 rounded text-danger font-mono">{selected.links.security_status.hidden_words.join(", ")}</code>
                          </p>
                        )}
                      </div>
                    )}

                    <div className="p-6 bg-haze/50 rounded-xl border hairline">
                      <div className="text-xs font-mono uppercase tracking-wider text-soft mb-2">Extracted Resume Preview</div>
                      <pre className="text-xs font-sans text-sub whitespace-pre-wrap max-h-[360px] overflow-y-auto leading-relaxed">
                        {(selected.resume_text || "No text extracted.").slice(0, 3000)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <CandidateDetail 
                    c={selected} 
                    projectId={id} 
                    onKit={onKit} 
                    kitBusy={kitBusy} 
                    tab={tab} 
                    setTab={setTab} 
                    onEditLinks={() => setLinksFor(selected.id)} 
                    onReanalyze={() => onAnalyzeSingleClick(selected)}
                    reanalyzing={analyzingSingleId === selected.id}
                    onPromptResume={(inline) => promptCandidateResume(selected, inline)}
                    onPromptBrief={() => promptCandidateBriefPdf(selected)}
                    onUpdateCandidateKit={(nextKit) => {
                      setData(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          candidates: prev.candidates.map(cand => cand.id === selected.id ? { ...cand, interview_kit: nextKit } : cand)
                        };
                      });
                    }}
                  />
                )}
              </div>
            </section>
          </div>
        )}

        {/* COMPARE VIEW */}
        {view === "compare" && (
          <Compare candidates={compareCandidates} />
        )}

        {/* LINKS EDIT DIALOG */}
        {linksFor && (
          <LinksDialog
            candidate={candidates.find(c => c.id === linksFor)}
            projectId={id}
            onClose={() => setLinksFor(null)}
            onSaved={async () => { setLinksFor(null); await load(); }}
          />
        )}

        {/* ADD RESUMES DIALOG */}
        {showUploadModal && (
          <AddResumesDialog
            projectId={id}
            existingCount={candidates.length}
            onClose={() => setShowUploadModal(false)}
            onUploaded={async () => {
              setShowUploadModal(false);
              await load();
            }}
          />
        )}

        {/* CONFIRM ANALYSIS DIALOG */}
        {confirmModal && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmModal(null)}>
            <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-6 bg-white shadow-lift space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent">Run AI Screening</div>
                  <h3 className="text-xl font-serif font-light mt-1 tracking-tight text-ink">Confirm Analysis Run</h3>
                </div>
                <button onClick={() => setConfirmModal(null)} className="text-soft hover:text-ink">
                  <XIcon size={18} />
                </button>
              </div>
              <p className="text-xs text-sub leading-relaxed">
                This analysis run will screen <strong>{confirmModal.label}</strong> against your job description and hiring criteria using your available platform credits (or configured API key).
              </p>
              <div className="flex justify-end gap-2 pt-3 border-t hairline">
                <button onClick={() => setConfirmModal(null)} className="btn-ghost py-2 px-4 text-xs">
                  Cancel
                </button>
                <button onClick={executeAnalysis} className="btn-accent py-2 px-5 text-xs flex items-center gap-1.5">
                  <Play size={12} /> Confirm & Run
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BATCH INTERVIEW KIT GENERATION CONFIRMATION MODAL */}
        {kitBatchModal && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setKitBatchModal(null)}>
            <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-6 bg-white shadow-lift space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent">Interview Preparation</div>
                  <h3 className="text-xl font-serif font-light mt-1 tracking-tight text-ink">Generate Interview Kits</h3>
                </div>
                <button onClick={() => setKitBatchModal(null)} className="text-soft hover:text-ink">
                  <XIcon size={18} />
                </button>
              </div>
              <p className="text-xs text-sub leading-relaxed">
                Generate customized role-specific interview kits for <strong>{kitBatchModal.label}</strong>.
              </p>
              <div className="p-4 bg-haze2 rounded-xl text-xs space-y-2 border hairline">
                <div className="font-semibold text-ink flex items-center gap-1.5">
                  <Sparkles size={13} className="text-accent" /> What will be generated:
                </div>
                <ul className="list-disc pl-4 space-y-1 text-sub font-sans text-[11px]">
                  <li>Targeted Technical, Behavioral, Problem-Solving, and Leadership questions.</li>
                  <li>Custom evaluation guidelines and validation concerns for each candidate.</li>
                  <li>Recruiter summary and recommended interview strategy.</li>
                </ul>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t hairline">
                <button onClick={() => setKitBatchModal(null)} className="btn-ghost py-2 px-4 text-xs">
                  Cancel
                </button>
                <button onClick={executeBatchKits} className="btn-accent py-2 px-5 text-xs flex items-center gap-1.5">
                  <Sparkles size={12} /> Start Kit Generation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UNIFIED DOWNLOAD CONFIRMATION MODAL */}
        {downloadModal && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDownloadModal(null)}>
            <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-lg p-7 bg-white shadow-lift space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-accent font-bold px-2 py-0.5 rounded-full bg-accentSoft">
                    {downloadModal.badge}
                  </span>
                  <h3 className="text-2xl font-serif font-light mt-2 tracking-tight text-ink">{downloadModal.title}</h3>
                  <p className="text-xs text-sub mt-1">{downloadModal.subtitle}</p>
                </div>
                <button onClick={() => setDownloadModal(null)} className="text-soft hover:text-ink p-1">
                  <XIcon size={18} />
                </button>
              </div>

              <div className="p-4 bg-haze2 rounded-xl border hairline space-y-2.5">
                <div className="flex justify-between items-center text-xs font-mono">
                  <span className="text-soft uppercase">Scope:</span>
                  <span className="font-semibold text-ink">{downloadModal.candidateScope}</span>
                </div>
                <div className="text-xs text-sub leading-relaxed pt-2 border-t hairline">
                  {downloadModal.description}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-1.5 text-[11px] font-mono text-soft">
                  <HelpCircle size={12} /> Ready for instant download
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDownloadModal(null)} className="btn-ghost py-2 px-4 text-xs">
                    Cancel
                  </button>
                  <button onClick={triggerDownloadConfirmed} className="btn-accent py-2.5 px-6 text-xs flex items-center gap-2">
                    <Download size={13} /> Confirm &amp; Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONFIRM DELETE CANDIDATE DIALOG */}
        {deleteTargetId && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDeleteTargetId(null)}>
            <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-6 bg-white shadow-lift space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-danger">Remove Candidate</div>
                  <h3 className="text-xl font-serif font-light mt-1 tracking-tight text-ink">Confirm Removal</h3>
                </div>
                <button onClick={() => setDeleteTargetId(null)} className="text-soft hover:text-ink">
                  <XIcon size={18} />
                </button>
              </div>
              <p className="text-xs text-sub leading-relaxed">
                Are you sure you want to remove this candidate and their resume from the project? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 pt-3 border-t hairline">
                <button onClick={() => setDeleteTargetId(null)} className="btn-ghost py-2 px-4 text-xs">
                  Cancel
                </button>
                <button onClick={confirmDeleteCandidate} className="bg-danger text-white rounded-full py-2 px-5 text-xs font-medium hover:bg-danger/90 transition">
                  Remove Candidate
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function CandidateDetail({ c, projectId, onKit, kitBusy, tab, setTab, onEditLinks, onReanalyze, reanalyzing, onPromptResume, onPromptBrief, onUpdateCandidateKit }) {
  const a = c.analysis;
  const k = c.interview_kit;
  const lk = c.links || {};
  const radarData = Object.entries(a.scores || {}).map(([k, v]) => ({ axis: k.replace(/_/g, " "), value: v }));
  const tabs = [["match", "Match"], ["skills", "Skills & Resume"], ["strengths", "Strengths"], ["weaknesses", "Risks"], ["interview", "Interview Kit"], ["verdict", "Verdict"]];
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [answersBusy, setAnswersBusy] = useState({});

  useEffect(() => {
    setExpandedQuestions({});
  }, [c.id]);

  const onGenerateAnswers = async (qidx) => {
    setAnswersBusy(prev => ({ ...prev, [qidx]: true }));
    try {
      const updatedQuestion = await Recruit.generateQuestionAnswers(projectId, c.id, qidx);
      toast.success("Reference answers generated!");
      
      const nextKit = { ...k };
      nextKit.questions = [...nextKit.questions];
      nextKit.questions[qidx] = {
        ...nextKit.questions[qidx],
        poor_answer: updatedQuestion.poor_answer,
        average_answer: updatedQuestion.average_answer,
        excellent_answer: updatedQuestion.excellent_answer
      };
      
      onUpdateCandidateKit(nextKit);
      setExpandedQuestions(prev => ({ ...prev, [qidx]: true }));
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Failed to generate answers: " + detail);
    } finally {
      setAnswersBusy(prev => ({ ...prev, [qidx]: false }));
    }
  };

  const candEmail = a.email || lk.email || "";
  const candPhone = a.phone || lk.phone || "";
  const candLocation = a.location || lk.location || "";
  const hasValidLinks = Boolean(lk.linkedin || lk.github || lk.portfolio || candEmail || candPhone);

  return (
    <div data-testid={`candidate-detail-${c.id}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <span className={classBadge(a.classification)}>{a.classification}</span>
          <h2 className="text-3xl lg:text-4xl font-serif font-light mt-3 leading-tight tracking-tight text-ink">{a.name}</h2>
          <div className="text-sub font-sans text-sm mt-1.5">
            {a.headline} · <span className="font-mono text-xs text-soft">{a.years_experience}y experience</span>
            {candLocation && <span className="font-mono text-xs text-soft"> · {candLocation}</span>}
          </div>
          {a.education && (
            <div className="text-xs font-mono text-soft mt-1">{a.education}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-wider text-soft">Match score</div>
          <div className="text-5xl lg:text-6xl font-mono font-bold text-accent leading-none mt-1 tabular-nums">{a.match_score}</div>
          <div className="text-xs text-soft mt-2 font-mono">Risk {a.risk_score} · Confidence {a.confidence}%</div>
        </div>
      </div>

      <p className="mt-6 text-sm text-sub max-w-3xl border-l-2 border-accent pl-4 font-sans leading-relaxed">{a.summary}</p>

      {/* Security Alerts */}
      {(lk.security_status?.white_ink_detected || lk.security_status?.prompt_injection_detected) && (
        <div className="mt-5 p-4 border border-dashed border-danger bg-danger/5 rounded-xl space-y-2 max-w-3xl">
          <div className="flex items-center gap-2 text-danger font-mono text-xs font-bold uppercase tracking-wider">
            <AlertTriangle size={14} /> Security Flag Detected
          </div>
          {lk.security_status?.prompt_injection_detected && (
            <p className="text-xs text-ink font-sans">
              ⚠️ <strong>Potential Prompt Injection:</strong> This resume contains text matching adversarial instruction templates.
            </p>
          )}
          {lk.security_status?.white_ink_detected && lk.security_status?.hidden_words && lk.security_status?.hidden_words.length > 0 && (
            <p className="text-xs text-ink font-sans">
              ⚠️ <strong>Hidden Text / Keyword Stuffing:</strong> Detected white/hidden text matching keywords: <code className="bg-haze px-1.5 py-0.5 rounded text-danger font-mono">{lk.security_status.hidden_words.join(", ")}</code>
            </p>
          )}
        </div>
      )}

      {/* Profile Links & Interviewer Download Bar */}
      <div className="mt-5 pt-4 border-t hairline flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {candEmail && (
            <a href={`mailto:${candEmail}`} className="badge badge-weak hover:bg-haze transition" data-testid="link-email" title="Email candidate">
              <Mail size={10} className="mr-1.5 inline" /> {candEmail}
            </a>
          )}
          {candPhone && (
            <a href={`tel:${candPhone}`} className="badge badge-weak hover:bg-haze transition" data-testid="link-phone" title="Call candidate">
              <Phone size={10} className="mr-1.5 inline" /> {candPhone}
            </a>
          )}
          {lk.linkedin && (
            <a href={lk.linkedin} target="_blank" rel="noreferrer" className="badge badge-weak hover:bg-haze transition" data-testid="link-linkedin">
              <Linkedin size={10} className="mr-1.5 inline" /> LinkedIn
            </a>
          )}
          {lk.github && (
            <a href={lk.github} target="_blank" rel="noreferrer" className="badge badge-weak hover:bg-haze transition" data-testid="link-github">
              <Github size={10} className="mr-1.5 inline" /> GitHub
            </a>
          )}
          {lk.portfolio && (
            <a href={lk.portfolio} target="_blank" rel="noreferrer" className="badge badge-weak hover:bg-haze transition" data-testid="link-portfolio">
              <Globe size={10} className="mr-1.5 inline" /> Portfolio
            </a>
          )}
          <button onClick={onEditLinks} className="text-xs font-medium text-accent hover:underline flex items-center gap-1" data-testid="edit-links-btn">
            <Link2 size={11} /> {hasValidLinks ? "Edit contact & links" : "Add Contact / Profiles"}
          </button>
        </div>

        {/* Direct Interviewer Downloads & Re-Analyze */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onReanalyze}
            disabled={reanalyzing}
            className="btn-ghost py-1.5 px-3 text-xs inline-flex items-center gap-1.5 border hairline bg-white hover:bg-haze"
            title="Re-run AI analysis for this candidate"
          >
            <RefreshCw size={12} className={reanalyzing ? "animate-spin" : ""} />
            {reanalyzing ? "Re-analyzing..." : "Re-analyze"}
          </button>
          <button
            onClick={() => onPromptResume(true)}
            className="btn-ghost py-1.5 px-3 text-xs inline-flex items-center gap-1.5 border hairline bg-white hover:bg-haze"
            title="View candidate's original resume in browser"
          >
            <Eye size={12} /> View Resume
          </button>
          <button
            onClick={() => onPromptResume(false)}
            className="btn-ghost py-1.5 px-3 text-xs inline-flex items-center gap-1.5 border hairline bg-white hover:bg-haze"
            title="Download candidate's original resume file"
          >
            <FileText size={12} /> Resume
          </button>
          <button
            onClick={onPromptBrief}
            data-testid="brief-pdf-btn"
            className="btn-accent py-1.5 px-3.5 text-xs inline-flex items-center gap-1.5"
            title="Download AI Evaluation & Interview Brief PDF for Interviewer"
          >
            <Download size={12} /> AI Brief PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b hairline mt-8 flex flex-wrap gap-1 overflow-x-auto">
        {tabs.map(([key, label]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider -mb-px border-t border-x border-transparent border-b-2 transition-all ${
              tab === key 
                ? "border-t border-x hairline border-b-transparent bg-white text-accent font-semibold rounded-t-xl" 
                : "text-sub hover:text-ink"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="pt-7">
        {tab === "match" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card p-5 bg-white">
              <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Score breakdown</div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E8E8ED" />
                    <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: "#1D1D1F", fontFamily: "Geist Mono" }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8, fill: "#86868B", fontFamily: "Geist Mono" }} />
                    <Radar dataKey="value" stroke="#0071E3" fill="#0071E3" fillOpacity={0.15} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="space-y-4">
              {Object.entries(a.scores || {}).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-xs font-mono mb-1.5">
                    <span className="text-sub capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-semibold text-ink tabular-nums">{v}</span>
                  </div>
                  <div className="h-2 bg-haze border hairline rounded-full overflow-hidden">
                    <div style={{ width: `${v}%`, transition: "width 600ms ease" }} className="h-full bg-accent rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "skills" && (
          <div className="space-y-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Extracted Skills</div>
              <div className="flex flex-wrap gap-2">
                {(a.skills || []).map(s => (
                  <span key={s} className="badge badge-mod text-xs">{s}</span>
                ))}
              </div>
            </div>
            {a.education && (
              <div className="card p-5 bg-white">
                <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Education</div>
                <div className="text-sm font-sans text-ink">{a.education}</div>
              </div>
            )}
            {a.external_insights && (
              <div className="border border-dashed border-accent bg-accentSoft/30 p-5 rounded-xl">
                <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">External insights</div>
                <div className="text-sm font-sans text-ink">{a.external_insights}</div>
              </div>
            )}
            {c.resume_text && (
              <div className="card p-5 bg-haze/40">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-sub">Parsed Resume Text ({c.filename})</div>
                  <button
                    onClick={() => onPromptResume(false)}
                    className="text-xs font-mono text-accent hover:underline flex items-center gap-1"
                  >
                    <Download size={11} /> Download Original File
                  </button>
                </div>
                <pre className="text-xs font-sans text-sub whitespace-pre-wrap max-h-[340px] overflow-y-auto leading-relaxed">
                  {c.resume_text}
                </pre>
              </div>
            )}
          </div>
        )}

        {tab === "strengths" && (
          <div className="space-y-3">
            {(a.strengths || []).map((s, i) => (
              <div key={i} className="card p-4 bg-white flex gap-3">
                <div className="w-7 h-7 bg-success/10 text-success flex items-center justify-center shrink-0 rounded-xl">
                  <Check size={14} />
                </div>
                <div className="text-sm font-sans text-ink pt-0.5">{s}</div>
              </div>
            ))}
            <div className="card p-5 mt-4 bg-haze2">
              <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Why shortlist</div>
              <div className="text-sm font-sans text-ink">{a.why_shortlist}</div>
            </div>
          </div>
        )}

        {tab === "weaknesses" && (
          <div className="space-y-4">
            {(a.weaknesses || []).map((s, i) => (
              <div key={i} className="card p-4 bg-white flex gap-3">
                <div className="w-7 h-7 bg-warn/10 text-warn flex items-center justify-center shrink-0 rounded-xl">
                  <AlertTriangle size={14} />
                </div>
                <div className="text-sm font-sans text-ink pt-0.5">{s}</div>
              </div>
            ))}
            {a.missing_requirements?.length > 0 && (
              <div className="card p-5 bg-white">
                <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Missing requirements</div>
                <div className="flex flex-wrap gap-2">
                  {a.missing_requirements.map((m, i) => (
                    <span key={i} className="badge badge-risk text-xs">{m}</span>
                  ))}
                </div>
              </div>
            )}
            {a.red_flags?.length > 0 && (
              <div className="border border-dashed border-danger bg-danger/5 p-5 rounded-xl">
                <div className="text-xs font-mono uppercase tracking-[0.1em] text-danger mb-2 font-bold">Red flags</div>
                <ul className="list-disc pl-5 text-sm font-sans text-ink space-y-1">
                  {a.red_flags.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            <div className="card p-5 bg-haze2">
              <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Concerns</div>
              <div className="text-sm font-sans text-ink">{a.why_concerns}</div>
            </div>
          </div>
        )}

        {tab === "interview" && (
          <div className="space-y-5">
            {!k ? (
              <div className="card p-12 text-center bg-white">
                <div className="w-12 h-12 bg-accentSoft text-accent flex items-center justify-center mx-auto mb-4 rounded-2xl">
                  <Sparkles size={16} />
                </div>
                <h3 className="text-xl font-serif font-light mb-2">Generate the Interview Kit</h3>
                <p className="text-xs font-sans text-sub max-w-md mx-auto mb-5">Sharp, validation-focused questions tailored to this candidate's strengths and risks.</p>
                <button onClick={onKit} disabled={kitBusy} data-testid="gen-kit-btn"
                        className="btn-accent inline-flex items-center gap-2 py-3 px-6">
                  {kitBusy ? "Generating…" : "Generate Interview Kit"}
                </button>
              </div>
            ) : k.status === "processing" ? (
              <div className="card p-12 text-center bg-white">
                <div className="w-12 h-12 bg-accentSoft text-accent flex items-center justify-center mx-auto mb-4 rounded-2xl animate-spin">
                  <Sparkles size={16} />
                </div>
                <h3 className="text-xl font-serif font-light mb-2">Generating Interview Kit...</h3>
                <p className="text-xs font-sans text-sub max-w-md mx-auto mb-5">
                  Analyzing profile, GitHub projects, and job description to craft custom questions and reference answers. This may take up to 45 seconds.
                </p>
                <div className="flex justify-center gap-1">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            ) : k.status === "failed" ? (
              <div className="card p-12 text-center bg-white font-sans">
                <div className="w-12 h-12 bg-danger/10 text-danger flex items-center justify-center mx-auto mb-4 rounded-2xl">
                  <AlertTriangle size={16} />
                </div>
                <h3 className="text-xl font-serif font-light mb-2 text-danger">Generation Failed</h3>
                <p className="text-xs font-sans text-sub max-w-md mx-auto mb-5">
                  {k.error || "An unexpected error occurred during generation."}
                </p>
                <button onClick={onKit} disabled={kitBusy}
                        className="btn-accent inline-flex items-center gap-2 py-3 px-6">
                  {kitBusy ? "Retrying…" : "Try Again"}
                </button>
              </div>
            ) : (
              <>
                <div className="card p-5 bg-accentSoft/30">
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2 font-bold">Recruiter Brief</div>
                  <p className="text-sm font-sans text-ink leading-relaxed">{k.candidate_brief}</p>
                </div>
                <div className="card p-5 bg-white">
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Deep Brief</div>
                  <p className="text-sm font-sans text-ink leading-relaxed">{k.deep_brief}</p>
                </div>
                <div className="card p-5 bg-white">
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Strategy</div>
                  <p className="text-sm font-sans text-ink leading-relaxed">{k.interview_strategy}</p>
                </div>
                {k.compensation_estimate && (
                  <div className="card p-4 bg-white text-xs text-sub font-mono">
                    💰 Compensation Estimate: {k.compensation_estimate}
                  </div>
                )}
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Targeted Questions</div>
                  <div className="space-y-4">
                    {(k.questions || []).map((q, i) => (
                      <div key={i} className="card p-5 bg-white">
                        <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-accent font-bold">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-[10px] font-mono uppercase tracking-wider text-soft">{q.category}</span>
                          </div>
                          {q.poor_answer || q.average_answer || q.excellent_answer ? (
                            <button 
                              onClick={() => setExpandedQuestions(prev => ({ ...prev, [i]: !prev[i] }))}
                              className="text-xs font-mono font-medium text-accent hover:underline flex items-center gap-1"
                            >
                              {expandedQuestions[i] ? "Hide Guide Answers" : "Show Reference Answers"}
                            </button>
                          ) : (
                            <button 
                              onClick={() => onGenerateAnswers(i)}
                              disabled={answersBusy[i]}
                              className="text-xs font-mono font-medium text-accent hover:underline flex items-center gap-1.5"
                            >
                              {answersBusy[i] ? (
                                <>
                                  <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin"></span>
                                  <span>Generating...</span>
                                </>
                              ) : (
                                "💡 Generate Reference Answers"
                              )}
                            </button>
                          )}
                        </div>
                        <div className="font-serif font-light text-lg leading-snug text-ink">{q.question}</div>
                        {q.what_to_look_for && (
                          <div className="text-xs text-sub mt-3 pt-3 border-t hairline">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-soft font-semibold">Look for: </span>
                            {q.what_to_look_for}
                          </div>
                        )}
                        {expandedQuestions[i] && (
                          <div className="mt-4 pt-4 border-t hairline space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {q.poor_answer && (
                                <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl">
                                  <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-red-600 mb-1.5 flex items-center gap-1">
                                    🔴 Poor Answer (Warning)
                                  </div>
                                  <p className="text-xs font-sans text-red-900 leading-relaxed">{q.poor_answer}</p>
                                </div>
                              )}
                              {q.average_answer && (
                                <div className="p-3.5 bg-amber-50 border border-amber-100 rounded-xl">
                                  <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-amber-600 mb-1.5 flex items-center gap-1">
                                    🟡 Acceptable Answer (Baseline)
                                  </div>
                                  <p className="text-xs font-sans text-amber-900 leading-relaxed">{q.average_answer}</p>
                                </div>
                              )}
                              {q.excellent_answer && (
                                <div className="p-3.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                                  <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-600 mb-1.5 flex items-center gap-1">
                                    🟢 Standout Answer (Expert)
                                  </div>
                                  <p className="text-xs font-sans text-emerald-950 leading-relaxed">{q.excellent_answer}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end">
                  <button onClick={onKit} disabled={kitBusy} 
                          className="btn-ghost text-xs py-2 px-4">
                    {kitBusy ? "Regenerating…" : "Regenerate Questions"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "verdict" && (
          <div className="space-y-4">
            <div className="card p-7 bg-haze">
              <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Final Verdict</div>
              <div className="font-serif font-light text-2xl leading-snug text-ink">{k?.final_verdict || a.why_shortlist}</div>
            </div>
            {k?.validation_concerns && (
              <div className="card p-5 bg-white">
                <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-2">Validation Concerns</div>
                <ul className="list-disc pl-5 text-sm font-sans text-ink space-y-1">
                  {k.validation_concerns.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LinksDialog({ candidate, projectId, onClose, onSaved }) {
  const initial = candidate?.links || {};
  const [email, setEmail] = useState(candidate?.analysis?.email || initial.email || "");
  const [phone, setPhone] = useState(candidate?.analysis?.phone || initial.phone || "");
  const [linkedin, setLinkedin] = useState(initial.linkedin || "");
  const [github, setGithub] = useState(initial.github || "");
  const [portfolio, setPortfolio] = useState(initial.portfolio || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await Recruit.setLinks(projectId, candidate.id, { linkedin, github, portfolio, email, phone });
      toast.success("Contact details and links saved.");
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="links-dialog">
      <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-7 bg-white shadow-lift max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent">Contact & Profiles</div>
            <h3 className="text-xl font-serif font-light mt-1 tracking-tight text-ink">{candidate?.analysis?.name || candidate?.filename}</h3>
          </div>
          <button onClick={onClose} className="text-soft hover:text-ink" data-testid="close-links"><XIcon size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-ink flex items-center gap-2 mb-1.5"><Mail size={12} /> Email Address</label>
            <input data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="candidate@example.com" className="input-apple text-sm" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-ink flex items-center gap-2 mb-1.5"><Phone size={12} /> Mobile / Phone</label>
            <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 9876543210 or (555) 000-0000" className="input-apple text-sm" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-ink flex items-center gap-2 mb-1.5"><Linkedin size={12} /> LinkedIn URL</label>
            <input data-testid="linkedin-input" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/…" className="input-apple text-sm" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-ink flex items-center gap-2 mb-1.5"><Github size={12} /> GitHub URL</label>
            <input data-testid="github-input" value={github} onChange={(e) => setGithub(e.target.value)} placeholder="https://github.com/…" className="input-apple text-sm" />
            <div className="text-[10px] font-mono text-soft mt-1.5">Public repositories and bio will be parsed during analysis.</div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-ink flex items-center gap-2 mb-1.5"><Globe size={12} /> Portfolio URL</label>
            <input data-testid="portfolio-input" value={portfolio} onChange={(e) => setPortfolio(e.target.value)} placeholder="https://…" className="input-apple text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-7 pt-5 border-t hairline">
          <button onClick={onClose} className="btn-ghost py-2 px-4 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-links"
                  className="btn-accent py-2 px-5 text-xs disabled:opacity-50">
            {busy ? "Saving…" : "Save details"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Compare({ candidates }) {
  if (candidates.length < 2) {
    return (
      <div className="card p-12 text-center bg-white">
        <div className="w-10 h-10 bg-haze flex items-center justify-center mx-auto mb-4 rounded-xl"><FileText size={20} className="text-soft" /></div>
        <h3 className="text-xl font-serif font-light mb-2">Pick 2–4 analyzed candidates to compare</h3>
        <p className="text-xs text-sub">Use the checkboxes next to candidates in the leaderboard.</p>
      </div>
    );
  }
  const axes = Array.from(new Set(candidates.flatMap(c => Object.keys(c.analysis.scores || {}))));
  const data = axes.map(ax => {
    const row = { axis: ax.replace(/_/g, " ") };
    candidates.forEach(c => row[c.analysis.name || c.filename] = (c.analysis.scores || {})[ax] || 0);
    return row;
  });
  const colors = ["#0071E3", "#AF52DE", "#34C759", "#FF9F0A"];
  const overall = candidates.map(c => ({ name: (c.analysis.name || c.filename).slice(0, 18), score: c.analysis.match_score, risk: c.analysis.risk_score }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-6 bg-white">
          <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Skill radar</div>
          <div style={{ height: 360 }}>
            <ResponsiveContainer>
              <RadarChart data={data}>
                <PolarGrid stroke="#E8E8ED" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: "#1D1D1F", fontFamily: "Geist Mono" }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8, fill: "#86868B", fontFamily: "Geist Mono" }} />
                {candidates.map((c, i) => (
                  <Radar key={c.id} dataKey={c.analysis.name || c.filename} stroke={colors[i % colors.length]} fill={colors[i % colors.length]} fillOpacity={0.1} strokeWidth={2} />
                ))}
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "Geist" }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card p-6 bg-white">
          <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent mb-3">Match vs Risk</div>
          <div style={{ height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={overall} barCategoryGap={20}>
                <CartesianGrid stroke="#E8E8ED" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#1D1D1F", fontFamily: "Geist" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#1D1D1F", fontFamily: "Geist" }} />
                <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E8E8ED", background: "#FFFFFF", fontFamily: "Geist", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "Geist" }} />
                <Bar dataKey="score" fill="#0071E3" radius={[4, 4, 0, 0]} />
                <Bar dataKey="risk" fill="#FF3B30" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="grid gap-px bg-line" style={{ gridTemplateColumns: `200px repeat(${candidates.length}, minmax(0,1fr))` }}>
          <div className="bg-haze p-3 text-xs font-mono uppercase tracking-wider text-sub">Field</div>
          {candidates.map(c => <div key={c.id} className="bg-haze p-3 font-serif font-light text-base text-ink truncate">{c.analysis.name || c.filename}</div>)}
          {[
            ["Match score", c => c.analysis.match_score],
            ["Classification", c => c.analysis.classification],
            ["Risk score", c => c.analysis.risk_score],
            ["Years exp", c => c.analysis.years_experience],
            ["Top skills", c => (c.analysis.skills || []).slice(0, 5).join(", ")],
            ["Why shortlist", c => c.analysis.why_shortlist],
          ].map(([label, fn], idx) => (
            <div key={`row-${idx}`} className="contents">
              <div className="bg-haze2 p-3 text-xs font-mono uppercase tracking-wider text-sub">{label}</div>
              {candidates.map(c => <div key={c.id + idx} className="bg-white p-3 font-sans text-sm text-ink">{fn(c)}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddResumesDialog({ projectId, existingCount, onClose, onUploaded }) {
  const [resumes, setResumes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const remainingSlots = Math.max(0, 50 - existingCount);

  const handleFiles = (incoming) => {
    const valid = Array.from(incoming || []);
    if (existingCount + resumes.length + valid.length > 50) {
      const allowed = Math.max(0, 50 - existingCount - resumes.length);
      toast.error(`Maximum 50 resumes allowed per project. You can add at most ${allowed} more.`);
      setResumes((prev) => [...prev, ...valid.slice(0, allowed)]);
    } else {
      setResumes((prev) => [...prev, ...valid]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeResume = (index) => {
    setResumes((prev) => prev.filter((_, i) => i !== index));
  };

  const upload = async () => {
    if (resumes.length === 0) {
      toast.error("Please select at least one resume to upload.");
      return;
    }
    setBusy(true);
    setUploadProgress({ currentChunk: 1, totalChunks: Math.ceil(resumes.length / 4), uploadedCount: 0, totalFiles: resumes.length });
    try {
      const res = await Recruit.uploadResumes(projectId, resumes, (prog) => {
        setUploadProgress(prog);
      });
      toast.success(`Successfully added ${res.uploaded?.length || resumes.length} resume(s)!`);
      onUploaded();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Failed to upload resumes: " + detail);
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" 
      onClick={onClose} 
      data-testid="add-resumes-dialog"
    >
      <div 
        onClick={(e) => e.stopPropagation()} 
        className="card w-full max-w-lg p-7 bg-white shadow-lift space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.1em] text-accent">Expand Candidate Pool</div>
            <h3 className="text-xl font-serif font-light mt-1 tracking-tight text-ink">Add Resumes to Project</h3>
            <p className="text-xs text-sub mt-0.5">
              Current: <span className="font-mono font-medium text-ink">{existingCount}/50</span> candidates ({remainingSlots} slot{remainingSlots === 1 ? "" : "s"} available)
            </p>
          </div>
          <button onClick={onClose} className="text-soft hover:text-ink p-1" data-testid="close-upload-modal">
            <XIcon size={18} />
          </button>
        </div>

        {remainingSlots <= 0 ? (
          <div className="p-4 border border-dashed border-danger bg-danger/5 rounded-xl text-xs text-danger">
            This project has already reached the maximum limit of 50 candidates.
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)} 
            onDrop={handleDrop}
            onClick={() => document.getElementById("modal-resume-input").click()}
            data-testid="modal-resume-dropzone"
            className={`border border-dashed hairline p-8 text-center cursor-pointer transition-all rounded-xl ${
              drag ? "bg-ink text-white" : "bg-haze/30 hover:bg-haze"
            }`}
          >
            <Upload size={22} className="mx-auto mb-2 text-ink" />
            <div className="text-xs font-semibold uppercase tracking-wider text-ink">Drop additional resumes here</div>
            <div className="text-[10px] text-soft mt-1">PDF, DOCX, TXT or MD · click to browse</div>
            <input 
              id="modal-resume-input" 
              data-testid="modal-resume-input" 
              type="file" 
              multiple 
              accept=".pdf,.docx,.txt,.md"
              onChange={(e) => handleFiles(e.target.files)} 
              className="hidden" 
            />
          </div>
        )}

        {resumes.length > 0 && (
          <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            {resumes.map((r, i) => (
              <div key={i} className="flex items-center justify-between border hairline px-3.5 py-2.5 text-xs bg-haze2 rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={12} className="text-soft shrink-0" />
                  <span className="font-mono text-xs truncate text-ink">{r.name}</span>
                </div>
                <button 
                  onClick={() => removeResume(i)} 
                  className="text-soft hover:text-danger p-1 transition-all"
                  disabled={busy}
                >
                  <XIcon size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-[11px] font-mono text-soft pt-2">
          <span>{resumes.length} new resume{resumes.length === 1 ? "" : "s"} selected</span>
          <span>{remainingSlots - resumes.length} slot{remainingSlots - resumes.length === 1 ? "" : "s"} left</span>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t hairline">
          <button onClick={onClose} disabled={busy} className="btn-ghost py-2 px-4 text-xs">
            Cancel
          </button>
          <button 
            onClick={upload} 
            disabled={busy || resumes.length === 0} 
            data-testid="confirm-upload-resumes-btn"
            className="btn-accent py-2 px-5 text-xs disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy ? (
              <>
                <span className="dot"/><span className="dot"/><span className="dot"/> 
                {uploadProgress ? `Uploading batch ${uploadProgress.currentChunk}/${uploadProgress.totalChunks}...` : "Uploading..."}
              </>
            ) : (
              <>
                <Upload size={13} /> Add {resumes.length > 0 ? `${resumes.length} resumes` : "resumes"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
